/**
 * User Health Score Calculator
 * Calculates a comprehensive health score (0-100) based on multiple factors
 */

/**
 * Calculate user health score
 * @param {Object} user - User object with all data
 * @returns {Object} - { score, breakdown, category }
 */
export const calculateHealthScore = (user) => {
    let score = 0;
    const breakdown = {
        shakeActivity: 0,
        engagement: 0,
        referrals: 0,
        tenure: 0,
    };

    // 1. Engagement Score (50 points)
    const engagementScore = calculateEngagementScore(user);
    breakdown.engagement = engagementScore;
    score += engagementScore;

    // 2. Referral Score (30 points)
    const referralScore = calculateReferralScore(user);
    breakdown.referrals = referralScore;
    score += referralScore;

    // 3. Tenure Score (20 points)
    const tenureScore = calculateTenureScore(user);
    breakdown.tenure = tenureScore;
    score += tenureScore;

    // Determine category
    const category = getHealthCategory(score);

    return {
        score: Math.round(score),
        breakdown,
        category,
    };
};



/**
 * Calculate engagement score (0-50 points)
 */
const calculateEngagementScore = (user) => {
    let score = 0;

    // Daily records logged: +25 points
    const dailyRecords = user.dailyRecords?.records || [];
    if (dailyRecords.length >= 10) {
        score += 25;
    } else if (dailyRecords.length >= 5) {
        score += 15;
    } else if (dailyRecords.length >= 1) {
        score += 8;
    }

    // Appointments attended: +17 points
    const appointments = user.appointments || [];
    const completedAppointments = appointments.filter(apt => apt.status === 'completed');
    if (completedAppointments.length >= 5) {
        score += 17;
    } else if (completedAppointments.length >= 3) {
        score += 12;
    } else if (completedAppointments.length >= 1) {
        score += 7;
    }

    // Profile completeness: +8 points
    const profileScore = calculateProfileCompleteness(user);
    score += profileScore;

    return Math.min(score, 50);
};

/**
 * Calculate referral score (0-30 points)
 */
const calculateReferralScore = (user) => {
    const referralCount = user.referralCount || 0;

    // 15 points per active referral (max 30)
    const score = Math.min(referralCount * 15, 30);

    return score;
};

/**
 * Calculate tenure score (0-20 points)
 */
const calculateTenureScore = (user) => {
    const createdAt = user.createdAt || user.registrationDate;
    if (!createdAt) return 0;

    const createdDate = createdAt.seconds
        ? new Date(createdAt.seconds * 1000)
        : new Date(createdAt);

    const daysSinceJoined = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
    const monthsSinceJoined = daysSinceJoined / 30;

    if (monthsSinceJoined >= 12) {
        return 20;
    } else if (monthsSinceJoined >= 6) {
        return 10;
    } else if (monthsSinceJoined >= 3) {
        return 6;
    } else if (monthsSinceJoined >= 1) {
        return 2;
    }

    return 0;
};

/**
 * Calculate profile completeness (0-8 points)
 */
const calculateProfileCompleteness = (user) => {
    let completedFields = 0;
    const totalFields = 8;

    if (user.personalDetails?.name) completedFields++;
    if (user.personalDetails?.email) completedFields++;
    if (user.personalDetails?.mobile) completedFields++;
    if (user.personalDetails?.address) completedFields++;
    if (user.personalDetails?.dateOfBirth) completedFields++;
    if (user.personalDetails?.gender) completedFields++;
    if (user.purpose) completedFields++;
    if (user.healthConditions && user.healthConditions.length > 0) completedFields++;

    return Math.round((completedFields / totalFields) * 8);
};

/**
 * Get health category based on score
 */
const getHealthCategory = (score) => {
    if (score >= 80) return 'VIP';
    if (score >= 60) return 'Active';
    if (score >= 40) return 'At Risk';
    if (score >= 20) return 'Inactive';
    return 'Critical';
};

/**
 * Get user segment based on health score and activity
 */
export const getUserSegment = (user, healthScore) => {
    const score = healthScore?.score || calculateHealthScore(user).score;
    const daysSinceLastActivity = getDaysSinceLastActivity(user);
    const referralCount = user.referralCount || 0;

    // VIP Users: High engagement, regular top-ups, active referrals
    if (score >= 75 && referralCount >= 2) {
        return {
            segment: 'VIP',
            color: 'purple',
            priority: 1,
            description: 'High-value user with excellent engagement'
        };
    }

    // Champions: High referrals and engagement
    if (referralCount >= 3 && score >= 60) {
        return {
            segment: 'Champion',
            color: 'gold',
            priority: 2,
            description: 'Top referrer with strong engagement'
        };
    }

    // At-Risk Users: Declining activity
    if (score < 40) {
        return {
            segment: 'At Risk',
            color: 'orange',
            priority: 3,
            description: 'Needs immediate attention to prevent churn'
        };
    }

    // Dormant Users: No activity in 60+ days
    if (daysSinceLastActivity >= 60) {
        return {
            segment: 'Dormant',
            color: 'red',
            priority: 4,
            description: 'Inactive for extended period'
        };
    }

    // New Users: Joined within last 30 days
    const daysSinceJoined = getDaysSinceJoined(user);
    if (daysSinceJoined <= 30) {
        return {
            segment: 'New User',
            color: 'blue',
            priority: 5,
            description: 'Recently onboarded'
        };
    }

    // Active Users: Regular engagement
    return {
        segment: 'Active',
        color: 'green',
        priority: 6,
        description: 'Regular user with good engagement'
    };
};

/**
 * Get days since last activity
 */
const getDaysSinceLastActivity = (user) => {
    const lastActivityDate = user.lastActivityDate || user.lastTopUpDate || user.createdAt;
    if (!lastActivityDate) return 999;

    const date = lastActivityDate.seconds
        ? new Date(lastActivityDate.seconds * 1000)
        : new Date(lastActivityDate);

    return Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
};

/**
 * Get days since joined
 */
const getDaysSinceJoined = (user) => {
    const createdAt = user.createdAt || user.registrationDate;
    if (!createdAt) return 0;

    const date = createdAt.seconds
        ? new Date(createdAt.seconds * 1000)
        : new Date(createdAt);

    return Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
};

/**
 * Get color for health score
 */
export const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    if (score >= 20) return 'text-orange-600';
    return 'text-red-600';
};

/**
 * Get background color for health score
 */
export const getHealthScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    if (score >= 20) return 'bg-orange-100';
    return 'bg-red-100';
};
